import React from "react";
import styled from "styled-components";
import { Fade } from "react-awesome-reveal";
import { ArrowLeft, MapPin, PhoneCall } from "lucide-react";
import p1 from '../images/p1.jpeg'

const DetailSection = styled.section`
  padding: 40px 16px;
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #2563eb;
  font-weight: 700;
  text-decoration: none;
  margin-bottom: 24px;
  transition: transform 0.2s;

  &:hover {
    transform: translateX(-4px);
  }
`;

const HeaderWrapper = styled.div`
  text-align: center;
  margin-bottom: 30px;

  h1 {
    font-size: clamp(2rem, 4vw, 2.8rem);
    font-weight: 900;
    color: #0a192f;
    margin: 0 0 8px 0;

    span {
      color: #2563eb;
    }
  }

  p {
    font-size: 1.05rem;
    color: #64748b;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

const MainContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  align-items: center;

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ImageDisplayCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  border: 2px solid #e2e8f0;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  padding: 12px;

  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
    object-fit: cover;
    display: block;
  }
`;

const InfoCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DescriptionBox = styled.div`
  background: #ffffff;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);

  h3 {
    font-size: 1.2rem;
    font-weight: 800;
    color: #0a192f;
    margin-bottom: 8px;
  }

  p {
    font-size: 0.95rem;
    color: #334155;
    line-height: 1.6;
    margin: 0;
  }
`;

const TableSection = styled.div`
  margin-top: 40px;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  h2 {
    background: #0a192f;
    color: #ffffff;
    padding: 16px 20px;
    margin: 0;
    font-size: 1.3rem;
    font-weight: 800;
    text-align: center;
    letter-spacing: 0.5px;
  }
`;

const ResponsiveTableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: center;

  th, td {
    padding: 14px 12px;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    background-color: #f1f5f9;
    color: #1e293b;
    font-size: 0.85rem;
    font-weight: 800;
    text-transform: uppercase;
  }

  td {
    font-size: 0.95rem;
    color: #334155;
    font-weight: 600;
  }

  tr:hover {
    background-color: #f8fafc;
  }

  .price-tag {
    color: #2563eb;
    font-weight: 900;
  }
`;

const ContactActionContainer = styled.div`
  margin-top: 30px;
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #2563eb;
  color: #ffffff;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: 800;
  font-size: 1rem;
  text-decoration: none;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
  transition: all 0.3s ease;

  &:hover {
    background-color: #1d4ed8;
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #0a192f;
  color: #ffffff;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: 800;
  font-size: 1rem;
  text-decoration: none;
  box-shadow: 0 4px 14px rgba(10, 25, 47, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background-color: #1e293b;
    transform: translateY(-2px);
  }
`;

// Property Data structured for Access Gate Estate
const accessGateHouseTypes = [
  { type: "4 Bedrooms Terrace Duplex", size: "180 sqm", price: "₦18,000,000" },
  { type: "4 Bedrooms Terrace Duplex", size: "250 sqm", price: "₦25,000,000" },
  { type: "5 Bedrooms Terrace Duplex with BQ", size: "300 sqm", price: "₦32,000,000" },
  { type: "4 Bedrooms Fully-Detached Penthouse with BQ", size: "450 sqm", price: "₦45,000,000" },
  { type: "5 Bedrooms Fully-Detached Duplex with BQ", size: "550 sqm", price: "₦55,000,000" },
];

const AccessGateDetails = () => {
  return (
    <DetailSection>
      <Container>
        <BackButton href="/">
          <ArrowLeft size={18} /> Back to Home
        </BackButton>

        <Fade direction="down" triggerOnce>
          <HeaderWrapper>
            <h1>Access Gate <span>Estate, Abuja</span></h1>
            <p><MapPin size={16} color="#2563eb" /> Premium Main-Gate Secured Smart Properties & Housing Units</p>
          </HeaderWrapper>
        </Fade>

        <MainContentGrid>
          <Fade direction="left" triggerOnce>
            <ImageDisplayCard>
              <img src={p1} alt="Access Gate Estate Layout and Units" />
            </ImageDisplayCard>
          </Fade>

          <Fade direction="right" triggerOnce>
            <InfoCard>
              <DescriptionBox>
                <h3>Project Overview</h3>
                <p>
                  Access Gate Estate is a premier residential community featuring high-security access control, smart architectural layouts, and fully serviced infrastructure. Designed to offer maximum comfort and safety for modern homeowners and investors.
                </p>
              </DescriptionBox>

                <DescriptionBox>
                  <h3>Key Features & Benefits</h3>
                  <p>
                    • Strategically located right by the main access gate for supreme visibility and control.<br />
                    • State-of-the-art perimeter fencing, smart CCTV surveillance, and 24/7 security patrol.<br />
                    • Flexible payment milestones and rapid physical allocation for subscribers.
                  </p>
              </DescriptionBox>
            </InfoCard>
          </Fade>
        </MainContentGrid>

        <Fade direction="up" triggerOnce>
          <TableSection>
            <h2>Official House Types, Sizes & Pricing Catalog (Access Gate)</h2>
            <ResponsiveTableWrapper>
              <StyledTable>
                <thead>
                  <tr>
                    <th>House Type / Category</th>
                    <th>Plot / Size (SQM)</th>
                    <th>Official Price</th>
                  </tr>
                </thead>
                <tbody>
                  {accessGateHouseTypes.map((item, index) => (
                    <tr key={index}>
                      <td><strong>{item.type}</strong></td>
                      <td>{item.size}</td>
                      <td className="price-tag">{item.price}</td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </ResponsiveTableWrapper>
          </TableSection>
        </Fade>

        <ContactActionContainer>
          <PrimaryButton href="tel:+2348121886498">
            <PhoneCall size={18} /> Call Sales Representative
          </PrimaryButton>
          <SecondaryButton href="https://wa.me/2348121886498" target="_blank" rel="noopener noreferrer">
            Chat on WhatsApp for Access Gate Units
          </SecondaryButton>
        </ContactActionContainer>
      </Container>
    </DetailSection>
  );
};

export default AccessGateDetails;